// NPM MODULES
import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';

// COMPONENTS
import Loader from '../../common/Loader';

// FORMATTED MESSAGES
import AllMessagesFormattedMessages from '../../../messages/reports/AllMessagesFormattedMessages';

// UTIL
import { errorCodes } from '../../../utils/errorCodes';

// NETWORK
import useAllMessagesRequest from '../../../requests/AllMessagesRequest';
import useSentRequest from '../../../requests/SentRequest';

const SentSmsSingle = ({ sms, errorHandler, redirectFromReply }) => {
  const [justCancelled, setJustCancelled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const { getSingleMessage } = useAllMessagesRequest();
  const { cancelPendingMessage } = useSentRequest();

  const getData = useCallback(async () => {
    try {
      if (redirectFromReply) {
        const result = await getSingleMessage(sms.sms_id, errorHandler);
        setMessage(result);
      } else {
        setMessage(sms);
      }
    } catch (error) {
      console.error('Error loading message data:', error);
      errorHandler?.(error);
    } finally {
      setLoading(false);
    }
  }, [sms, errorHandler, redirectFromReply]);

  useEffect(() => {
    getData();
  }, [getData]);

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
  };

  const showConfirmationModal = () => {
    setShowConfirmation(true);
  };

  const handleCancelPendingMessage = async () => {
    setIsSaving(true);
    const result = await cancelPendingMessage(message.sms_id, errorHandler);
    handleCloseConfirmation();
    if (result && result.length > 0) setJustCancelled(true);
    setIsSaving(false);
  };

  const generateBody = () => {
    if (!message) return <Typography gutterBottom>No Data</Typography>;

    return (
      <>
        <Typography fontWeight={700}>
          {AllMessagesFormattedMessages.queuedHeader}
        </Typography>
        <Typography gutterBottom>{message.date}</Typography>

        <Typography fontWeight={700}>
          {AllMessagesFormattedMessages.deliveredHeader}
        </Typography>
        <Typography gutterBottom>{message.delivered}</Typography>

        <Typography fontWeight={700}>
          {AllMessagesFormattedMessages.fromHeader}
        </Typography>
        <Typography gutterBottom>{message.from}</Typography>

        <Typography fontWeight={700}>
          {AllMessagesFormattedMessages.delDevice}
        </Typography>
        <Typography gutterBottom>{message.outdevice}</Typography>

        <Typography fontWeight={700}>
          {AllMessagesFormattedMessages.toHeader}
        </Typography>
        <Typography gutterBottom>{message.to}</Typography>

        <Typography fontWeight={700}>
          {AllMessagesFormattedMessages.repDevice}
        </Typography>
        <Typography gutterBottom>{message.indevice}</Typography>

        <Typography fontWeight={700}>
          {AllMessagesFormattedMessages.statusHeader}
        </Typography>
        <Typography gutterBottom>
          {justCancelled ? 'Cancelled' : message.status}
        </Typography>

        {generateErrorComponent(message.errorCode)}

        <Typography fontWeight={700}>
          {AllMessagesFormattedMessages.encodingHeader}
        </Typography>
        <Typography gutterBottom>{message.encoding}</Typography>
      </>
    );
  };

  const generateErrorComponent = (errorCode) => {
    if (!errorCode || typeof errorCode === 'undefined') return;
    const code = errorCodes(errorCode);
    return (
      <>
        <Typography fontWeight={700}>
          {AllMessagesFormattedMessages.errorCodeHeader}
        </Typography>
        <Typography gutterBottom>{code.roxCode}</Typography>
        <Typography fontWeight={700}>
          {AllMessagesFormattedMessages.errorTextHeader}
        </Typography>
        <Typography gutterBottom>{code.meaning}</Typography>
      </>
    );
  };

  return (
    <>
      {loading ? <Loader /> : generateBody()}

      <Dialog
        open={showConfirmation}
        onClose={handleCloseConfirmation}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title" variant="h2">
          Cancellation Confirmation
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you would like to cancel single pending messages?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleCloseConfirmation}
            disabled={isSaving}
          >
            No
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleCancelPendingMessage}
            disabled={isSaving}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SentSmsSingle;
